import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, Check, Image as ImageIcon, Package, Loader2 } from 'lucide-react';
import { Competitor, Product, ProductImage, ProductImageCategory } from '../../types/knowledge';
import knowledgeApiService from '../../services/api/knowledge/knowledgeApiService';

// 旧 ProductImageSelectionModal.scss を内製化（Wave 3d / T20）。
// 本 SCSS は `.modal-header` / `.modal-content` / `.modal-footer` / `.empty-state` 等を
// スコープなしのトップレベルで定義しており、後段の ProductSelectionModal.scss に
// 一部を上書きされていた。ここでは「マージ後の実効値」を内製化している。
//   - modal-title は 16px（PSM が 18px を上書き）/ close-button は 32px・角丸6px
//   - error-message の padding/背景/文字色は globals.css の `.error-message` が
//     勝っているためクラス名を残し、上書き分だけをユーティリティで表現
//   - product-list の gap:2px と footer のモバイル縦積みは PSM 由来の漏れ

export interface SelectedProductImage {
  id: number;
  url: string;
  s3Key: string; // S3 key for backend processing (avoiding CORS issues)
  category: ProductImageCategory;
  productId: number;
  productName: string;
  fileName: string;
  includeCompetitorAnalysis?: boolean;
  competitorSuggestions?: Competitor[];
  // Only meaningful when category === 'brand_logo'. null/undefined = tone not classified.
  logoTone?: 'light' | 'dark' | 'color' | null;
}

interface ProductImageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedImages: SelectedProductImage[]) => void;
  maxSelection?: number;
}

type Step = 'product' | 'images';
type CategoryFilter = ProductImageCategory | 'all';

const CATEGORY_CONFIG: Record<ProductImageCategory, { label: string; color: string }> = {
  brand_logo: { label: 'ブランドロゴ', color: '#3B82F6' },
  product_image: { label: '商品/サービス画像', color: '#22C55E' },
  material: { label: 'イメージ素材', color: '#A855F7' },
};

// Labels for brand_logo variation tone badges (light/dark/color の3種のみ; null は非表示)
const LOGO_TONE_LABELS: Record<'light' | 'dark' | 'color', string> = {
  light: '白系',
  dark: '黒系',
  color: 'カラー',
};

const ProductImageSelectionModal: React.FC<ProductImageSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  maxSelection = 1,
}) => {
  const [step, setStep] = useState<Step>('product');
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedImages, setSelectedImages] = useState<SelectedProductImage[]>([]);
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [competitorSuggestions, setCompetitorSuggestions] = useState<Competitor[]>([]);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);
  const [competitorError, setCompetitorError] = useState<string | null>(null);
  const [competitorRefreshStarted, setCompetitorRefreshStarted] = useState(false);
  const [includeCompetitorAnalysis, setIncludeCompetitorAnalysis] = useState(false);

  const resetCompetitorState = useCallback(() => {
    setCompetitorSuggestions([]);
    setLoadingCompetitors(false);
    setCompetitorError(null);
    setCompetitorRefreshStarted(false);
    setIncludeCompetitorAnalysis(false);
  }, []);

  // Fetch products when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    } else {
      // Reset state when modal closes
      setStep('product');
      setSelectedProduct(null);
      setProductImages([]);
      setSelectedImages([]);
      setActiveCategory('all');
      setError(null);
      resetCompetitorState();
    }
  }, [isOpen, resetCompetitorState]);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    setError(null);
    try {
      const data = await knowledgeApiService.getProducts({ status: 'active' });
      setProducts(data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setError('商品の取得に失敗しました');
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchProductImages = async (productId: number) => {
    setLoadingImages(true);
    setError(null);
    try {
      const data = await knowledgeApiService.getProductImages(productId);
      setProductImages(data);
    } catch (err) {
      console.error('Failed to fetch product images:', err);
      setError('商品画像の取得に失敗しました');
    } finally {
      setLoadingImages(false);
    }
  };

  const fetchCompetitorSuggestions = async (productId: number) => {
    setLoadingCompetitors(true);
    setCompetitorError(null);
    setCompetitorRefreshStarted(false);
    try {
      const competitors = await knowledgeApiService.getCompetitors(productId);
      setCompetitorSuggestions(competitors || []);

      if (!competitors || competitors.length === 0) {
        try {
          await knowledgeApiService.rediscoverCompetitors(productId);
          setCompetitorRefreshStarted(true);
        } catch (rediscoverErr) {
          console.warn('Failed to rediscover competitors:', rediscoverErr);
          setCompetitorError('競合候補の取得を開始できませんでした。画像選択は続行できます。');
        }
      }
    } catch (err) {
      console.warn('Failed to fetch competitors:', err);
      setCompetitorSuggestions([]);
      setCompetitorError('競合候補を取得できませんでした。画像選択は続行できます。');
    } finally {
      setLoadingCompetitors(false);
    }
  };

  const handleProductSelect = useCallback((product: Product) => {
    setSelectedProduct(product);
    setStep('images');
    setSelectedImages([]);
    resetCompetitorState();
    fetchProductImages(product.id);
    fetchCompetitorSuggestions(product.id);
  }, [resetCompetitorState]);

  const handleBack = useCallback(() => {
    setStep('product');
    setSelectedProduct(null);
    setProductImages([]);
    setSelectedImages([]);
    setActiveCategory('all');
    resetCompetitorState();
  }, [resetCompetitorState]);

  const createSelectedImage = useCallback((image: ProductImage): SelectedProductImage | null => {
    if (!selectedProduct) return null;

    return {
      id: image.id,
      url: image.url,
      s3Key: image.file_path,
      category: image.category,
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      fileName: image.file_name,
      includeCompetitorAnalysis,
      competitorSuggestions: includeCompetitorAnalysis ? competitorSuggestions : undefined,
      logoTone: image.logo_tone ?? null,
    };
  }, [competitorSuggestions, includeCompetitorAnalysis, selectedProduct]);

  const handleImageToggle = useCallback((image: ProductImage) => {
    if (!selectedProduct) return;

    setSelectedImages((prev) => {
      const existingIndex = prev.findIndex((img) => img.id === image.id);

      if (existingIndex >= 0) {
        // Remove if already selected
        return prev.filter((img) => img.id !== image.id);
      } else {
        // Add if not at max selection
        if (prev.length >= maxSelection) {
          // Replace the first selection if at max
          if (maxSelection === 1) {
            const selectedImage = createSelectedImage(image);
            return selectedImage ? [selectedImage] : prev;
          }
          return prev;
        }
        const selectedImage = createSelectedImage(image);
        return selectedImage ? [...prev, selectedImage] : prev;
      }
    });
  }, [createSelectedImage, selectedProduct, maxSelection]);

  const handleCompetitorToggle = useCallback((checked: boolean) => {
    setIncludeCompetitorAnalysis(checked);
    setSelectedImages((prev) => prev.map((img) => ({
      ...img,
      includeCompetitorAnalysis: checked,
      competitorSuggestions: checked ? competitorSuggestions : undefined,
    })));
  }, [competitorSuggestions]);

  useEffect(() => {
    setSelectedImages((prev) => prev.map((img) => ({
      ...img,
      includeCompetitorAnalysis,
      competitorSuggestions: includeCompetitorAnalysis ? competitorSuggestions : undefined,
    })));
  }, [competitorSuggestions, includeCompetitorAnalysis]);

  const handleConfirm = useCallback(() => {
    onSelect(selectedImages);
    onClose();
  }, [selectedImages, onSelect, onClose]);

  const filteredImages = activeCategory === 'all'
    ? productImages
    : productImages.filter((img) => img.category === activeCategory);

  const getCategoryCount = (category: ProductImageCategory) => {
    return productImages.filter((img) => img.category === category).length;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-6 max-[640px]:items-stretch max-[640px]:p-3"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[900px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] max-[640px]:max-h-[calc(100vh-24px)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header flex shrink-0 items-start justify-between rounded-t-lg border-b border-b-gray-200 bg-[#fafafa] px-6 py-5 max-[768px]:p-4">
          <div className="flex items-center gap-3">
            {step === 'images' && (
              <button
                className="back-button flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800"
                onClick={handleBack}
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <h2 className="flex items-center gap-2 text-[16px] font-semibold text-gray-800 max-[768px]:text-[15px]">
              {step === 'product' ? '商品を選択' : `${selectedProduct?.name} - 画像を選択`}
            </h2>
          </div>
          <button
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content flex min-h-0 w-full max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 [scrollbar-color:#c1c1c1_#f1f1f1] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb:hover]:bg-[#a8a8a8] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-[#f1f1f1] [&::-webkit-scrollbar]:w-2 max-[768px]:p-4">
          {error && (
            <div className="error-message mb-4 flex items-center justify-between rounded-lg border border-red-200">
              <span className="text-[14px] text-red-600">{error}</span>
              <button
                className="cursor-pointer rounded-md border-none bg-red-600 px-3 py-1.5 text-[13px] text-white transition-[background] duration-200 ease-[ease] hover:bg-red-700"
                onClick={step === 'product' ? fetchProducts : () => selectedProduct && fetchProductImages(selectedProduct.id)}
              >
                再試行
              </button>
            </div>
          )}

          {step === 'product' && (
            <div className="flex flex-col gap-0.5">
              {loadingProducts ? (
                <div className="loading-state flex flex-col items-center justify-center px-6 py-16 text-gray-500">
                  <Loader2 className="spinner mb-4 animate-spin text-blue-500" size={32} />
                  <p className="text-[14px]">商品を読み込み中...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="empty-state flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-[14px] text-gray-400">
                  <Package size={48} className="mb-4 opacity-50" />
                  <p>登録されている商品がありません</p>
                </div>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="flex cursor-pointer items-start gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4 transition-all duration-200 ease-[ease] hover:border-blue-500 hover:bg-white hover:shadow-[0_4px_12px_rgba(59,130,246,0.15)]"
                      onClick={() => handleProductSelect(product)}
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-gray-200 text-gray-500">
                        <Package size={24} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="product-name mb-1 overflow-hidden text-ellipsis whitespace-nowrap text-[15px] font-semibold text-gray-800">{product.name}</h3>
                        {product.description && (
                          <p className="product-description mb-2 line-clamp-2 overflow-hidden text-[13px] text-gray-500">{product.description}</p>
                        )}
                        <span className="inline-flex items-center gap-1 text-[12px] text-gray-400">
                          <ImageIcon size={14} className="opacity-70" />
                          {product.product_images?.length || 0} 枚の画像
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'images' && (
            <div className="flex flex-col gap-5">
              <div className="rounded-[10px] border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start justify-between gap-4 max-[640px]:flex-col max-[640px]:gap-3">
                  <div>
                    <h3 className="mb-1 text-[14px] font-semibold text-gray-800">競合候補</h3>
                    <p className="text-[13px] text-gray-500">
                      {loadingCompetitors
                        ? '選択した商品の競合候補を取得しています'
                        : competitorSuggestions.length > 0
                        ? `${competitorSuggestions.length}件の候補を取得済み`
                        : competitorRefreshStarted
                        ? '競合調査を開始しました。完了後に候補が表示されます'
                        : '候補はまだありません'}
                    </p>
                  </div>
                  <label className="inline-flex min-h-[28px] shrink-0 cursor-pointer items-center gap-2 text-[13px] font-semibold text-gray-800 max-[640px]:w-full">
                    <input
                      type="checkbox"
                      className="m-0 h-4 w-4 cursor-pointer accent-blue-500"
                      checked={includeCompetitorAnalysis}
                      onChange={(event) => handleCompetitorToggle(event.target.checked)}
                    />
                    <span>競合情報を分析に含める</span>
                  </label>
                </div>

                {competitorSuggestions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {competitorSuggestions.slice(0, 3).map((competitor) => (
                      <span key={competitor.id} className="inline-flex max-w-[220px] items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-[999px] border border-blue-100 bg-white px-2.5 py-[5px] text-[12px] font-medium text-blue-700">
                        {competitor.name}
                      </span>
                    ))}
                    {competitorSuggestions.length > 3 && (
                      <span className="muted inline-flex max-w-[220px] items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-[999px] border border-gray-200 bg-white px-2.5 py-[5px] text-[12px] font-medium text-gray-500">他 {competitorSuggestions.length - 3} 件</span>
                    )}
                  </div>
                )}

                {competitorError && (
                  <p className="mt-2.5 text-[13px] text-amber-700">{competitorError}</p>
                )}
              </div>

              {/* Category Tabs */}
              <div className="mb-5 flex flex-wrap gap-2 max-[640px]:flex-nowrap max-[640px]:overflow-x-auto max-[640px]:pb-1">
                <button
                  className={
                    activeCategory === 'all'
                      ? 'active inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-[color:var(--category-color,#3b82f6)] bg-[color:var(--category-color,#3b82f6)] px-2 py-[5px] text-[11px] font-medium text-white transition-all duration-200 ease-[ease]'
                      : 'inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-gray-200 bg-gray-100 px-2 py-[5px] text-[11px] font-medium text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-200'
                  }
                  onClick={() => setActiveCategory('all')}
                >
                  すべて
                  <span
                    className={
                      activeCategory === 'all'
                        ? 'count rounded-[10px] bg-white/20 px-2 py-0.5 text-[12px] text-white'
                        : 'count rounded-[10px] bg-gray-200 px-2 py-0.5 text-[12px] text-gray-500'
                    }
                  >
                    {productImages.length}
                  </span>
                </button>
                {(Object.keys(CATEGORY_CONFIG) as ProductImageCategory[]).map((category) => (
                  <button
                    key={category}
                    className={
                      activeCategory === category
                        ? 'active inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-[color:var(--category-color,#3b82f6)] bg-[color:var(--category-color,#3b82f6)] px-2 py-[5px] text-[11px] font-medium text-white transition-all duration-200 ease-[ease]'
                        : 'inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-gray-200 bg-gray-100 px-2 py-[5px] text-[11px] font-medium text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-200'
                    }
                    onClick={() => setActiveCategory(category)}
                    style={{
                      '--category-color': CATEGORY_CONFIG[category].color,
                    } as React.CSSProperties}
                  >
                    {CATEGORY_CONFIG[category].label}
                    <span
                      className={
                        activeCategory === category
                          ? 'count rounded-[10px] bg-white/20 px-2 py-0.5 text-[12px] text-white'
                          : 'count rounded-[10px] bg-gray-200 px-2 py-0.5 text-[12px] text-gray-500'
                      }
                    >
                      {getCategoryCount(category)}
                    </span>
                  </button>
                ))}
              </div>

              {/* Images Grid */}
              {loadingImages ? (
                <div className="loading-state flex flex-col items-center justify-center px-6 py-16 text-gray-500">
                  <Loader2 className="spinner mb-4 animate-spin text-blue-500" size={32} />
                  <p className="text-[14px]">画像を読み込み中...</p>
                </div>
              ) : filteredImages.length === 0 ? (
                <div className="empty-state flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-[14px] text-gray-400">
                  <ImageIcon size={48} className="mb-4 opacity-50" />
                  <p>
                    {activeCategory === 'all'
                      ? '登録されている画像がありません'
                      : `${CATEGORY_CONFIG[activeCategory as ProductImageCategory].label}が登録されていません`}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
                  {filteredImages.map((image) => {
                    const isSelected = selectedImages.some((img) => img.id === image.id);
                    return (
                      <div
                        key={image.id}
                        className={
                          isSelected
                            ? 'selected cursor-pointer overflow-hidden rounded-xl border-2 border-blue-500 bg-white shadow-[0_0_0_3px_rgba(59,130,246,0.2)] transition-all duration-200 ease-[ease]'
                            : 'cursor-pointer overflow-hidden rounded-xl border-2 border-gray-200 bg-white transition-all duration-200 ease-[ease] hover:border-blue-500 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]'
                        }
                        onClick={() => handleImageToggle(image)}
                      >
                        <div className="relative aspect-[4/3] w-full bg-[#f5f5f5]">
                          <img src={image.url} alt={image.title || image.file_name} className="h-full w-full object-contain" />
                          {isSelected && (
                            <div className="absolute inset-0 flex items-center justify-center bg-blue-500/60 text-white">
                              <Check size={24} />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <span
                            className="mb-1.5 inline-block rounded px-2 py-[3px] text-[11px] font-medium text-white"
                            style={{ backgroundColor: CATEGORY_CONFIG[image.category].color }}
                          >
                            {CATEGORY_CONFIG[image.category].label}
                          </span>
                          {image.category === 'brand_logo' && image.logo_tone === 'light' && (
                            <span className="mb-1.5 ml-1.5 inline-block rounded border border-gray-300 bg-gray-50 px-2 py-[3px] text-[11px] font-medium text-gray-700">
                              {LOGO_TONE_LABELS.light}
                            </span>
                          )}
                          {image.category === 'brand_logo' && image.logo_tone === 'dark' && (
                            <span className="mb-1.5 ml-1.5 inline-block rounded bg-gray-800 px-2 py-[3px] text-[11px] font-medium text-white">
                              {LOGO_TONE_LABELS.dark}
                            </span>
                          )}
                          {image.category === 'brand_logo' && image.logo_tone === 'color' && (
                            <span className="mb-1.5 ml-1.5 inline-block rounded bg-[linear-gradient(135deg,#f472b6,#60a5fa)] px-2 py-[3px] text-[11px] font-medium text-white">
                              {LOGO_TONE_LABELS.color}
                            </span>
                          )}
                          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px] text-gray-800">{image.title || image.file_name}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer flex shrink-0 items-center justify-between gap-4 border-t border-t-gray-200 bg-gray-50 px-6 py-4 max-[768px]:flex-col max-[768px]:p-4 max-[640px]:gap-3">
          {selectedImages.length > 0 && (
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="shrink-0 text-[13px] text-gray-500">選択中:</span>
              <div className="flex gap-2 overflow-x-auto">
                {selectedImages.map((img) => (
                  <div key={img.id} className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border-2 border-blue-500">
                    <img src={img.url} alt={img.fileName} className="h-full w-full bg-[#f5f5f5] object-contain" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex shrink-0 gap-3 max-[640px]:justify-end">
            <button
              className="cursor-pointer rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-[14px] font-medium text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800 max-[768px]:w-full max-[768px]:justify-center"
              onClick={onClose}
            >
              キャンセル
            </button>
            <button
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border-none bg-blue-500 px-6 py-2.5 text-[14px] font-medium text-white transition-all duration-200 ease-[ease] hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-400 max-[768px]:w-full max-[768px]:justify-center"
              onClick={handleConfirm}
            >
              {selectedImages.length === 0 ? '選択をクリア' : `選択 (${selectedImages.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductImageSelectionModal;
